import { useMemo, useCallback } from "react";
import {
  Box,
  Checkbox,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { EditOutlined, OpenInNew, ReceiptOutlined } from "@mui/icons-material";
import { LIFECYCLE_STATUS, getEffectiveStatus } from "../types/common";
import type { V2InvoiceListItem } from "../types/invoice";
import StatusBadge from "./StatusBadge";
import { getStatusLabel, EDITABLE_LIFECYCLE_STATUSES } from "../constants/lifecycleStatus";
import { convertToINR, convertDate } from "@/helpers/Utils";
import { V2_COLORS } from "../constants/colors";
import { usePermissions } from "@/context/PermissionsContext";
import { BOUNCER_PERMISSIONS } from "@/constants/bouncerPermissions";

// --- Types ---

type ColumnDef = {
  key: string;
  header: string;
  align?: "left" | "right" | "center";
  render: (invoice: V2InvoiceListItem) => React.ReactNode;
  /** When set, column only renders if this returns true */
  visible?: boolean;
};

interface InvoiceTableProps {
  invoices: V2InvoiceListItem[];
  onRowClick: (invoice: V2InvoiceListItem) => void;
  onReview?: (invoice: V2InvoiceListItem) => void;
  onEdit?: (invoice: V2InvoiceListItem) => void;
  onDelete?: (invoice: V2InvoiceListItem) => void;
  onCreditNote?: (invoice: V2InvoiceListItem) => void;
  selectedIds?: number[];
  onSelectionChange?: (ids: number[]) => void;
}

// --- Styles ---

const thStyle = {
  fontWeight: 700,
  fontSize: "12px",
  lineHeight: "16px",
  color: V2_COLORS.textSecondary,
  textTransform: "uppercase" as const,
  whiteSpace: "nowrap" as const,
};

const tdStyle = {
  fontSize: "14px",
  py: "16px",
};

// --- Row actions ---

type RowActions = { edit?: boolean };

export function getRowActions(
  invoice: V2InvoiceListItem,
  hasPermission: (key: string) => boolean,
): RowActions {
  return {
    edit:
      hasPermission(BOUNCER_PERMISSIONS.INVOICE_EDIT) &&
      !!invoice.lifecycle_status &&
      EDITABLE_LIFECYCLE_STATUSES.has(invoice.lifecycle_status),
  };
}

// --- Component ---

export default function InvoiceTable({
  invoices,
  onRowClick,
  onReview,
  onEdit,
  onDelete: _onDelete,
  onCreditNote,
  selectedIds = [],
  onSelectionChange,
}: InvoiceTableProps) {
  const { hasPermission } = usePermissions();
  const isInternal = hasPermission(BOUNCER_PERMISSIONS.ACCOUNTS_PAYABLE_VIEW);
  const isVendor = !isInternal;
  const showBulkSelect = !!onSelectionChange;

  const allSelected = invoices.length > 0 && selectedIds.length === invoices.length;

  const handleSelectAll = useCallback(() => {
    if (!onSelectionChange) return;
    onSelectionChange(allSelected ? [] : invoices.map((i) => i.id));
  }, [onSelectionChange, allSelected, invoices]);

  const handleSelectOne = useCallback(
    (id: number) => {
      if (!onSelectionChange) return;
      onSelectionChange(
        selectedIds.includes(id)
          ? selectedIds.filter((s) => s !== id)
          : [...selectedIds, id]
      );
    },
    [onSelectionChange, selectedIds]
  );

  // Column definitions — memoized so they don't get rebuilt on every render
  const columns = useMemo<ColumnDef[]>(() => {
    const cols: ColumnDef[] = [
      {
        key: "invoice_no",
        header: "Invoice No.",
        render: (inv) => (
          <Typography fontSize="14px" sx={{ whiteSpace: "nowrap" }}>{inv.invoice_no}</Typography>
        ),
      },
      {
        key: "vendor_name",
        header: "Vendor",
        visible: !isVendor,
        render: (inv) => (
          <Typography fontSize="14px" sx={{ whiteSpace: "nowrap" }}>{inv.vendor_name}</Typography>
        ),
      },
      {
        key: "po_code",
        header: "PO ID",
        render: (inv) => (
          <Box sx={{ whiteSpace: "nowrap" }}>
            <Typography fontSize="14px">{inv.po_code}</Typography>
            {isInternal && inv.business_unit && (
              <Typography fontSize="13px">
                <strong>BU:</strong> {inv.business_unit}
              </Typography>
            )}
            {inv.project_city && (
              <Typography fontSize="13px">
                <strong>City:</strong> {inv.project_city}
              </Typography>
            )}
          </Box>
        ),
      },
      {
        key: "invoice_date",
        header: "Invoice Date",
        render: (inv) => convertDate(inv.invoice_date),
      },
      {
        key: "invoice_value",
        header: "Invoice Value",
        align: "right",
        render: (inv) => convertToINR(inv.invoice_value),
      },
      {
        key: "review",
        header: "Invoice",
        render: () => (
          <Box display="inline-flex" alignItems="center" gap={0.5} sx={{ cursor: "pointer" }}>
            <Typography component="span" sx={{ fontSize: "13px", fontWeight: 600, color: V2_COLORS.primary }}>
              Review
            </Typography>
            <OpenInNew sx={{ fontSize: 14, color: V2_COLORS.primary }} />
          </Box>
        ),
      },
      {
        key: "credit_note",
        header: "Credit Note",
        render: (inv) => {
          const cn = inv.credit_notes?.[0];
          if (!cn) return "\u2014";
          return (
            <Box display="inline-flex" alignItems="center" gap={0.5} sx={{ cursor: "pointer" }}>
              <ReceiptOutlined sx={{ fontSize: 14, color: V2_COLORS.primary }} />
              <Typography component="span" sx={{ fontSize: "13px", fontWeight: 600, color: V2_COLORS.primary }}>
                {cn.number}
              </Typography>
            </Box>
          );
        },
      },
      {
        key: "grn_amount",
        header: "GRN Amount",
        visible: !isVendor,
        align: "right",
        render: (inv) => inv.grn_amount != null ? convertToINR(inv.grn_amount) : "\u2014",
      },
      {
        key: "lifecycle_status",
        header: "Status",
        render: (inv) => (
          <StatusBadge
            status={getEffectiveStatus(inv)}
            displayLabel={inv.invoice_status?.label ?? getStatusLabel(getEffectiveStatus(inv), isInternal, inv.po_source)}
          />
        ),
      },
      {
        key: "remarks",
        header: "Remarks",
        visible: isVendor,
        render: (inv) =>
          getEffectiveStatus(inv) === LIFECYCLE_STATUS.REJECTED ? (
            <Typography variant="body2" sx={{ color: V2_COLORS.error }}>
              Rejected
            </Typography>
          ) : (
            "\u2014"
          ),
      },
    ];

    return cols.filter((col) => col.visible !== false);
  }, [isVendor, isInternal]);

  // +1 for actions column, +1 for checkbox column if bulk select
  const colCount = columns.length + 1 + (showBulkSelect ? 1 : 0);

  return (
    <TableContainer sx={{ overflow: "visible" }}>
      <Table stickyHeader>
        <TableHead>
          <TableRow sx={{ bgcolor: "white", "& th": { borderBottomWidth: 2, borderColor: V2_COLORS.border } }}>
            {showBulkSelect && (
              <TableCell padding="checkbox">
                <Checkbox
                  size="small"
                  checked={allSelected}
                  indeterminate={selectedIds.length > 0 && !allSelected}
                  onChange={handleSelectAll}
                />
              </TableCell>
            )}
            {columns.map((col) => (
              <TableCell key={col.key} sx={thStyle} align={col.align}>
                {col.header}
              </TableCell>
            ))}
            <TableCell sx={thStyle} align="center">
              Actions
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {invoices.length === 0 && (
            <TableRow>
              <TableCell colSpan={colCount} align="center">
                <Typography color="textSecondary" py={4}>
                  No invoices found
                </Typography>
              </TableCell>
            </TableRow>
          )}
          {invoices.map((invoice) => {
            const actions = getRowActions(invoice, hasPermission);
            const isSelected = selectedIds.includes(invoice.id);

            return (
              <TableRow
                key={invoice.id}
                hover
                selected={isSelected}
                sx={{ cursor: "pointer", "&:last-child td": { borderBottom: 0 } }}
                onClick={() => onRowClick(invoice)}
              >
                {showBulkSelect && (
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      size="small"
                      checked={isSelected}
                      onChange={() => handleSelectOne(invoice.id)}
                    />
                  </TableCell>
                )}
                {columns.map((col) => (
                  <TableCell
                    key={col.key}
                    sx={tdStyle}
                    align={col.align}
                    {...(col.key === "review" ? {
                      onClick: (e: React.MouseEvent) => { e.stopPropagation(); onReview?.(invoice); },
                    } : col.key === "credit_note" && invoice.credit_notes?.[0] ? {
                      onClick: (e: React.MouseEvent) => { e.stopPropagation(); onCreditNote?.(invoice); },
                    } : {})}
                  >
                    {col.render(invoice)}
                  </TableCell>
                ))}
                <TableCell align="center" onClick={(e) => e.stopPropagation()} sx={{ whiteSpace: "nowrap" }}>
                  {actions.edit && (
                    <IconButton size="small" onClick={() => onEdit?.(invoice)}>
                      <EditOutlined sx={{ fontSize: 20, color: V2_COLORS.textSecondary }} />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
